import { useInfiniteQuery } from 'react-query';
import { FeaturedContent } from 'rwl-shared/dist/models';
import { useAPIRoute } from '../../hooks/useAPIRoute';

type UseFetchContentParams = Readonly<{
    accountId: string;
    enabled: boolean;
    postCode?: string;
    pageSize?: number;
}>;

type FetchError = {
    message: string;
    status: number;
};

type FetchContentParams = Readonly<{
    pageParam?: number;
    accountId: string;
    postCode?: string;
    url: string;
}>;

const fetchContent = async ({
    pageParam = 0,
    accountId,
    postCode,
    url,
}: FetchContentParams): Promise<FeaturedContent> => {
    try {
        const params = new URLSearchParams({
            accountId,
            postCodes: postCode || '',
            page: pageParam.toString(),
        });

        const result = await fetch(`${url}?${params}`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Cache-Control': 'no-cache', // Prevent browser caching since we use react-query
            },
        });

        if (!result.ok) {
            throw new Error(`HTTP error! status: ${result.status}`);
        }

        return result.json();
    } catch (error) {
        const errorMessage =
            error instanceof Error ? error.message : 'Unknown error occurred';
        throw {
            message: errorMessage,
            status: error instanceof Response ? error.status : 500,
        } as FetchError;
    }
};

const hasMoreContent = (lastPage: FeaturedContent) => {
    return Boolean(
        lastPage.jobs?.length ||
            lastPage.similarJobs?.length ||
            lastPage.reviews?.length ||
            lastPage.similarReviews?.length
    );
};

export const useFetchInfiniteContent = ({
    accountId,
    enabled,
    postCode,
    pageSize = 50,
}: UseFetchContentParams) => {
    const url = useAPIRoute('/serviceareas/featured', 'v1');

    return useInfiniteQuery<FeaturedContent, FetchError>(
        ['useFetchInfiniteContent', accountId, postCode, pageSize],
        ({ pageParam = 0 }) =>
            fetchContent({ accountId, postCode, url, pageParam }),
        {
            getNextPageParam: (
                lastPage: FeaturedContent,
                pages: FeaturedContent[]
            ) => (hasMoreContent(lastPage) ? pages.length : undefined),
            enabled,
            staleTime: 1000 * 60 * 60 * 24, // 24 hours
            cacheTime: 1000 * 10, // With map bounds, we'll rarely have a cache hit, but we don't want cache to bloat
            refetchOnWindowFocus: false, // Prevent unnecessary refetches
            retry: 2, // Retry failed requests twice
            retryDelay: (attemptIndex: number) =>
                Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
        }
    );
};
